/**
 * Razorpay Payment Service
 * Frontend service layer for handling Razorpay payment operations
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { firebaseApp } from '../firebase.js';

const functions = getFunctions(firebaseApp);

/**
 * Create a new Razorpay order
 */
export const createRazorpayOrder = async (
  amount,
  planType,
  planDuration,
  email,
  notes
) => {
  try {
    const createOrder = httpsCallable(functions, 'createRazorpayOrder');

    const result = await createOrder({
      amount,
      currency: 'INR',
      planType,
      planDuration,
      email: email || null,
      notes: notes || {},
    });

    const data = result.data;

    if (!data.success) {
      throw new Error('Failed to create order');
    }

    return {
      success: data.success,
      orderId: data.orderId,
      amount: data.amount,
      amountInRupees: data.amountInRupees,
      currency: data.currency,
      receipt: data.receipt,
    };
  } catch (error) {
    console.error('Error creating Razorpay order:', error);
    throw new Error(error.message || 'Failed to create order');
  }
};

/**
 * Verify Razorpay payment signature
 */
export const verifyRazorpayPayment = async (
  orderId,
  paymentId,
  signature
) => {
  try {
    const verifyPayment = httpsCallable(functions, 'verifyRazorpayPayment');

    const result = await verifyPayment({
      orderId,
      paymentId,
      signature,
    });

    const data = result.data;

    if (!data.success) {
      throw new Error(data.message || 'Payment verification failed');
    }

    return data;
  } catch (error) {
    console.error('Error verifying payment:', error);
    throw new Error(error.message || 'Failed to verify payment');
  }
};

/**
 * Initialize Razorpay checkout
 */
export const initializeRazorpayCheckout = (
  options
) => {
  // Check if Razorpay script is loaded
  if (!window.Razorpay) {
    throw new Error('Razorpay SDK not loaded. Please include the Razorpay script.');
  }

  const razorpay = new window.Razorpay(options);
  razorpay.open();
};

/**
 * Load Razorpay script dynamically
 */
export const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    // Check if script is already loaded
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;

    script.onload = () => {
      resolve(true);
    };

    script.onerror = () => {
      console.error('Failed to load Razorpay script');
      resolve(false);
    };

    document.body.appendChild(script);
  });
};

/**
 * Handle Razorpay payment flow
 * Complete flow: create order → open checkout → verify payment
 */
export const handleRazorpayPayment = async (
  amount,
  planType,
  planDuration,
  userDetails,
  notes = {},
  onSuccess,
  onFailure
) => {
  try {
    // Step 1: Load Razorpay script
    const isScriptLoaded = await loadRazorpayScript();
    if (!isScriptLoaded) {
      throw new Error('Failed to load Razorpay payment gateway');
    }

    // Step 2: Create order (pass email for signup flow)
    // Merge user details with custom notes (e.g., selectedPlan for HR)
    const orderNotes = {
      userName: userDetails.name,
      userEmail: userDetails.email,
      ...notes, // Include custom notes like selectedPlan
    };

    const order = await createRazorpayOrder(
      amount,
      planType,
      planDuration,
      userDetails.email, // Email parameter for unauthenticated signup
      orderNotes
    );

    console.log('Order created:', order.orderId);

    // Step 3: Get Razorpay Key ID from environment
    const razorpayKeyId = process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_RYmk9nQj1d2gyb';

    console.log('Razorpay Key ID:', razorpayKeyId ? 'Loaded' : 'Missing');

    if (!razorpayKeyId) {
      throw new Error('Razorpay Key ID not configured. Please set REACT_APP_RAZORPAY_KEY_ID in .env');
    }

    // Step 4: Initialize Razorpay checkout
    const options = {
      key: razorpayKeyId,
      amount: order.amount,
      currency: order.currency,
      name: 'VetriaAI',
      description: `${planType} - ${planDuration}`,
      // image: '/logo.png', // Optional - comment out if logo doesn't exist
      order_id: order.orderId,
      handler: async (response) => {
        try {
          console.log('Payment successful, verifying...');

          // Step 5: Verify payment on backend
          const verification = await verifyRazorpayPayment(
            response.razorpay_order_id,
            response.razorpay_payment_id,
            response.razorpay_signature
          );

          console.log('Payment verified:', verification);
          onSuccess(response);
        } catch (error) {
          console.error('Payment verification failed:', error);
          onFailure(error);
        }
      },
      prefill: {
        name: userDetails.name,
        email: userDetails.email,
        contact: userDetails.contact || '',
      },
      notes: {
        planType,
        planDuration,
        ...notes, // Include custom notes like selectedPlan
      },
      theme: {
        color: '#3b82f6', // Tailwind blue-500
      },
      modal: {
        ondismiss: () => {
          console.log('Payment modal closed by user');
          onFailure(new Error('Payment cancelled by user'));
        },
      },
    };

    initializeRazorpayCheckout(options);
  } catch (error) {
    console.error('Error in payment flow:', error);
    onFailure(error);
  }
};

/**
 * Get plan details by type and duration
 */
export const getPlanDetails = (planType, planDuration) => {
  const jobSeekerFeatures = [
    'Unlimited personalized courses',
    'Priority support',
    'Advanced analytics',
    'Certificate of completion',
  ];

  const plans = {
    JOBSEEKER: {
      '1 month': {
        name: 'Premium - 1 Month',
        amount: 199,
        originalAmount: 299,
        currency: 'INR',
        duration: '1 month',
        features: jobSeekerFeatures,
      },
      '3 months': {
        name: 'Premium - 3 Months',
        amount: 549,
        originalAmount: 597,
        currency: 'INR',
        duration: '3 months',
        features: jobSeekerFeatures,
        savings: 'Save ₹48',
      },
      '6 months': {
        name: 'Premium - 6 Months',
        amount: 1179,
        originalAmount: 1194,
        currency: 'INR',
        duration: '6 months',
        features: jobSeekerFeatures,
        savings: 'Save ₹15',
      },
      '12 months': {
        name: 'Premium - 12 Months',
        amount: 2299,
        originalAmount: 2388,
        currency: 'INR',
        duration: '12 months',
        features: jobSeekerFeatures,
        savings: 'Save ₹89',
      },
    },
    HR: {
      CORE_1M: {
        name: 'Core - 1 Month',
        amount: 2999,
        originalAmount: 3999,
        currency: 'INR',
        duration: '1 month',
        features: [
          'Unlimited test creation',
          'Advanced AI screening',
          'Priority candidate sourcing',
          'Team collaboration tools',
          'Dedicated support manager',
        ],
      },
      CORE_3M: {
        name: 'Core - 3 Months',
        amount: 7999,
        originalAmount: 8999,
        currency: 'INR',
        duration: '3 months',
        savings: 'Save ₹1,000',
        features: [
          'All Core features',
          'Quarterly billing',
          'Bulk candidate export',
        ],
      },
      CORE_6M: {
        name: 'Core - 6 Months',
        amount: 14999,
        originalAmount: 17999,
        currency: 'INR',
        duration: '6 months',
        savings: 'Save ₹3,000',
        features: [
          'All Core features',
          'Half-yearly billing',
          'Premium analytics dashboard',
        ],
      },
      CORE_12M: {
        name: 'Core - 12 Months',
        amount: 29999,
        originalAmount: 35999,
        currency: 'INR',
        duration: '12 months',
        savings: 'Save ₹6,000',
        features: [
          'All Core features',
          'Annual billing best value',
          'API access',
        ],
      },
    },
  };

  if (planType === 'JOBSEEKER') {
    const durationKey = planDuration || '1 month';
    return plans.JOBSEEKER[durationKey] || plans.JOBSEEKER['1 month'];
  }

  if (planType === 'HR') {
    switch (planDuration) {
      case '1 month':
        return plans.HR.CORE_1M;
      case '3 months':
        return plans.HR.CORE_3M;
      case '6 months':
        return plans.HR.CORE_6M;
      case '12 months':
        return plans.HR.CORE_12M;
      default:
        return plans.HR.CORE_1M;
    }
  }

  throw new Error('Invalid plan type');
};

